// Thin auth shim. While Stream A (backend) lands, this returns a static
// "demo user" so the visual rebuild can be developed independently. When
// the real /api/auth/me endpoint exists, this swaps to fetching it.

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

export type Role = 'operator' | 'executive' | 'superadmin';

export interface AuthUser {
  id: string;
  email: string;
  role: Role;
  is_admin: boolean;  // derived (= superadmin); kept for backward compatibility
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState>({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

const PASSCODE_HEADER = 'x-demo-passcode';
const DEMO_PASSCODE = import.meta.env.VITE_DEMO_PASSCODE ?? 'arizona-c2-demo';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const res = await fetch('/api/auth/me', {
        credentials: 'include',
        headers: { [PASSCODE_HEADER]: DEMO_PASSCODE },
      });
      if (res.ok) {
        setUser(await res.json());
      } else {
        setUser(null);
      }
    } catch {
      // Backend not ready yet — fall back to a placeholder user so visual
      // dev can continue. Remove this fallback at cutover.
      setUser({ id: 'demo-local', email: 'demo@local', role: 'superadmin', is_admin: true });
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
    window.location.href = '/login';
  }

  useEffect(() => {
    refresh();
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, logout }}>{children}</AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
